import {
    Box,
    Stack,
    Typography
} from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { ApiRoutes } from "../../configuration/apiRoutes";
import { RestService } from "../../utils/axios";
import KlasisLoader from "../KlasisLoader";
import { SearchResponse } from "../../models/SearchResponse";
import { SearchRecordDto } from "../../models/SearchRecordDto";
import InfiniteScroll from "react-infinite-scroll-component";
import SearchHiglight from "../../utils/searchHighlight";
import EmptySearchResults from "../EmptySearchResults";
import { useTranslation } from "react-i18next";
import ClassifierRecordDetails from "./ClassifierRecordDetails";
import { Translations } from "../../localization/translations";
import { Dayjs } from "dayjs";
import ClassifierRecordActions from "./ClassifierRecordActions";
import { GetCurrentAbsoluteUrl } from "../../configuration/routes";
import {ClassifierStructureExistenceInfoResponse} from "../../models/ClassifierStructureExistenceInfoResponse";

interface IClassifierRecordsLinearViewProps {
    classifierId: string;
    classifierLocale: string;
    level?: number;
    searchPhrase?: string;
    dateFilterValue?: Dayjs | null;
    structuresExistenceInfo: ClassifierStructureExistenceInfoResponse;
    getTreeRecordsDateFilter: () => [(Date | undefined), (Date | undefined), (Date | undefined)];
}

interface IClassifierRecordsLinearViewState {
    hasMore: boolean;
    page: number;
    items: Array<SearchRecordDto>;
}

const ClassifierRecordsLinearView: React.FC<IClassifierRecordsLinearViewProps> = (
    props: IClassifierRecordsLinearViewProps
) => {
    const [data, setData] = useState<IClassifierRecordsLinearViewState>({
        hasMore: true,
        items: [],
        page: 1
    });
    const [isLoadingData, setIsLoadingData] = useState(false);

    const { t } = useTranslation();

    useEffect(() => {
        setIsLoadingData(true);

        fetchMore(true);
    }, [props.level, props.searchPhrase, props.dateFilterValue, props.classifierLocale, props.structuresExistenceInfo]);

    const fetchMore = (resetData?: boolean): void => {
        const [onDate, validFromDate, validToDate] = props.getTreeRecordsDateFilter();

        if (!onDate && !validFromDate && !validToDate) {
            setData({
                hasMore: false,
                items: [],
                page: 1
            });
            setIsLoadingData(false);
            return;
        }

        const targetData = resetData ? {
            hasMore: true,
            items: [],
            page: 1
        } : data;

        RestService.Get<SearchResponse>(
            ApiRoutes.Search.GetClassificationRecordResults({
                classifierId: props.classifierId,
                localeCode: props.classifierLocale,
                pageNumber: targetData.page,
                maxLevel: props.level,
                searchPhrase: props.searchPhrase,
                // validFromDate == validToDate => filter records that will be active during that date will be selected
                validFromDate: onDate ? onDate : validFromDate,
                validToDate: onDate ? onDate : validToDate
            }),
            (response: SearchResponse) => {
                setData({
                    hasMore: response.hasNextPage ?? false,
                    items: targetData.items.concat(response.items),
                    page: targetData.page + 1
                });
            },
            (error: AxiosError) => {
                console.log("Error: " + error.message);
            },
            () => {
                setIsLoadingData(false);
            });
    }

    if (!isLoadingData && !data.items.length) {
      return (
        <EmptySearchResults
          title={t(Translations.classifier.label.noClassificationRecords)}
          description={t(Translations.classifier.label.noClassificationRecordsDescription)} />
      )
    }

    return (
        <>
            {isLoadingData ? <KlasisLoader /> : (
                <Stack padding={2}>
                    <InfiniteScroll
                        dataLength={data.items.length ?? 0}
                        next={fetchMore}
                        hasMore={data.hasMore}
                        loader={<KlasisLoader />}
                    >
                        {data?.items.map(item => (
                            <ClassifierRecordsLinearViewItem
                                data={item}
                                classifierId={props.classifierId}
                                classifierLocale={props.classifierLocale}
                                key={`item-${item.id}`} />
                        ))}
                    </InfiniteScroll>
                </Stack>
            )}
        </>
    );
}

const ItemColumnStyle = {
  display: 'flex',
  flexDirection: 'column'
}

interface IClassifierRecordsLinearViewItemProps {
    data: SearchRecordDto;
    classifierId: string;
    classifierLocale: string;
}

const ClassifierRecordsLinearViewItem: React.FC<IClassifierRecordsLinearViewItemProps> = (
    props: IClassifierRecordsLinearViewItemProps
) => {
    const [isDataExpanded, setIsDataExpanded] = useState(false);

    const renderTitle = (data: SearchRecordDto): string | JSX.Element | JSX.Element[] => {
        if (data.highlights.length && data.highlights[0].fragments.length) {
            return SearchHiglight.parse(data.highlights[0].fragments[0]);
        }

        return data.klasisData.title;
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
            borderBottomColor: 'grey.400',
            borderBottomWidth: 1,
            borderBottomStyle: 'solid'}}
        >
            <Box sx={ItemColumnStyle}>
                <Typography
                    paddingLeft={2}
                    color="text.secondary"
                    lineHeight="2.5rem"
                >
                    <b>
                        {props.data.klasisData.code}
                    </b>
                </Typography>
            </Box>
            <Box sx={{...ItemColumnStyle, flex: 1}}>
                <Typography
                    color="primary"
                    display="inline-block"
                    lineHeight="2.5rem"
                    marginLeft="1rem"
                >
                    {renderTitle(props.data)}
                </Typography>
                {isDataExpanded ? (
                    <Box sx={{marginLeft: '1rem'}}>
                        <ClassifierRecordDetails
                            classifierId={props.classifierId}
                            recordId={parseInt(props.data.id)}/>
                    </Box>) :
                    null}
            </Box>
            <Box sx={ItemColumnStyle}>
                <Box>
                    <ClassifierRecordActions
                        classifierId={props.classifierId}
                        classifierRecordCode={props.data.klasisData.code}
                        isDataExpanded={isDataExpanded}
                        localeCode={props.classifierLocale}
                        onExpandedStateChange={
                            (newExpandedState: boolean) => setIsDataExpanded(newExpandedState)
                        } />
                </Box>
            </Box>
        </Box>
    )
}

export default ClassifierRecordsLinearView;